import {
  AlertTriangle,
  BarChart3,
  CalendarRange,
  Check,
  Download,
  ExternalLink,
  FileDown,
  GripVertical,
  ImageDown,
  Inbox,
  LoaderCircle,
  MapPinned,
  MessageSquareText,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  useCallback,
  useEffect,
  forwardRef,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../auth/useAuth';
import { ReportDataWarnings } from '../components/ReportDataWarnings';
import { requireFirebaseFunctions } from '../lib/firebase';
import { formatReportGeneration, OUTPUT_REPORT_CALLABLE_OPTIONS } from '../lib/generated-report';
import {
  outputReportYears,
  type OutputReportKind,
  type ProcessingAvailability,
} from '../lib/processing-data';
import { svgElementMarkupForExport, svgMarkupDataUrl } from '../lib/svg-export';
import { CaseInflowPanel } from './output/CaseInflowPanel';
import { PecSocialSubreasonPanel } from './output/PecSocialSubreasonPanel';
import { SocialReasonEntryPanel } from './output/SocialReasonEntryPanel';
import { TerritorialAnalysisPanel } from './output/TerritorialAnalysisPanel';
import {
  createTrendWorkbook,
  trendWorkbookFileName,
  type TrendAnalysis,
  type TrendCategory,
} from '../lib/trend-analysis';

const DEFAULT_SERIES_KEYS = [
  'attivazione non completata',
  'mancato appuntamento',
  'mancata connessione rete',
];
const DEFAULT_SERIES_COLORS: Record<string, string> = {
  'attivazione non completata': '#5383ec',
  'mancato appuntamento': '#de3672',
  'mancata connessione rete': '#999999',
};
const ADDITIONAL_SERIES_COLORS = ['#2a8564', '#7c5cc4', '#d38732', '#3196a5', '#6b7d8a'];
const SHORT_MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MAX_CHART_SERIES = 5;

const MONTHLY_OUTPUT_KINDS: OutputReportKind[] = [
  'case-inflow', 'pec-social-subreason', 'social-reason-entry', 'territorial-analysis',
];
const demoAvailability: ProcessingAvailability = {
  masterYears: [2026, 2025, 2024],
  mail: [],
  responses: [],
  outputReports: [
    { kind: 'trend', year: 2026, months: [] },
    { kind: 'trend', year: 2025, months: [] },
    ...MONTHLY_OUTPUT_KINDS.flatMap((kind) => [
      { kind, year: 2026, months: [1, 2, 3, 4, 5, 6, 7, 8] },
      { kind, year: 2025, months: [12] },
    ]),
  ],
};

function demoTrendAnalysis(year: number): TrendAnalysis {
  const definitions: Array<[string, string, number[]]> = [
    ['mancata connessione rete', 'Mancata connessione rete', [640, 527, 500, 564, 586, 793, 867, 610, 0, 0, 0, 0]],
    ['mancato appuntamento', 'Mancato appuntamento', [517, 421, 401, 322, 297, 390, 520, 465, 0, 0, 0, 0]],
    ['attivazione non completata', 'Attivazione non completata', [450, 336, 365, 332, 311, 402, 490, 420, 0, 0, 0, 0]],
    ['civico non cablato', 'Civico non cablato', [85, 75, 130, 187, 257, 276, 242, 337, 0, 0, 0, 0]],
    ['varie', 'Varie', [0, 427, 429, 20, 30, 40, 45, 47, 0, 0, 0, 0]],
  ];
  const categories = definitions.map(([key, label, values]) => ({
    key,
    label,
    total: values.reduce((sum, value) => sum + value, 0),
  }));
  const months = SHORT_MONTHS.map((_, index) => {
    const values = definitions.map(([, , monthly]) => monthly[index]);
    return {
      month: index + 1,
      label: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'][index],
      values,
      total: values.reduce((sum, value) => sum + value, 0),
    };
  });
  const classifiedRows = categories.reduce((sum, category) => sum + category.total, 0);
  return {
    year,
    file: {
      id: 'demo-master',
      name: `${year}.xlsx`,
      path: `Export / Export annuale / ${year}.xlsx`,
      modifiedTime: '2026-09-22T18:47:00.000Z',
      webViewLink: null,
    },
    worksheetName: 'Report Segnalazioni Utenti Soci',
    sourceRows: 52084,
    classifiedRows,
    unclassifiedRows: 52084 - classifiedRows,
    categories,
    months,
  };
}

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('it-IT').format(value);
}

function defaultSeriesKeys(categories: TrendCategory[]): string[] {
  const preferred = DEFAULT_SERIES_KEYS.filter((key) => categories.some((category) => category.key === key));
  const remaining = categories
    .map(({ key }) => key)
    .filter((key) => !preferred.includes(key));
  return [...preferred, ...remaining].slice(0, 3);
}

function seriesColors(keys: string[]): string[] {
  let additionalIndex = 0;
  return keys.map((key) => {
    const defaultColor = DEFAULT_SERIES_COLORS[key];
    if (defaultColor) return defaultColor;
    const color = ADDITIONAL_SERIES_COLORS[additionalIndex % ADDITIONAL_SERIES_COLORS.length];
    additionalIndex += 1;
    return color;
  });
}

function moveSeries(keys: string[], sourceKey: string, targetKey: string, placeAfter: boolean): string[] {
  if (sourceKey === targetKey || !keys.includes(sourceKey) || !keys.includes(targetKey)) return keys;
  const reordered = keys.filter((key) => key !== sourceKey);
  const targetIndex = reordered.indexOf(targetKey);
  reordered.splice(targetIndex + (placeAfter ? 1 : 0), 0, sourceKey);
  return reordered;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?$/, '');
  }
  return 'Non è stato possibile generare l’analisi trend.';
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function niceAxisMaximum(maximum: number): { maximum: number; step: number } {
  if (maximum <= 0) return { maximum: 100, step: 25 };
  const roughStep = maximum / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = factor * magnitude;
  return { maximum: step * Math.ceil(maximum / step), step };
}

const TrendChart = forwardRef<SVGSVGElement, {
  analysis: TrendAnalysis;
  selectedKeys: string[];
}>(function TrendChart(props, svgRef) {
  const selected = props.selectedKeys
    .map((key) => {
      const index = props.analysis.categories.findIndex((category) => category.key === key);
      return index >= 0 ? { ...props.analysis.categories[index], index } : null;
    })
    .filter((category): category is TrendCategory & { index: number } => category !== null);
  const colors = seriesColors(props.selectedKeys);
  const highestValue = Math.max(0, ...props.analysis.months.flatMap((month) => (
    selected.map(({ index }) => month.values[index] ?? 0)
  )));
  const axis = niceAxisMaximum(highestValue);
  const chart = { x: 66, y: 36, width: 1024, height: 320 };
  const monthWidth = chart.width / 12;
  const availableBarWidth = monthWidth * 0.7;
  const barWidth = Math.min(18, availableBarWidth / Math.max(selected.length, 1));
  const groupWidth = barWidth * selected.length;
  const ticks = Array.from({ length: Math.round(axis.maximum / axis.step) + 1 }, (_, index) => index * axis.step);

  return (
    <svg
      ref={svgRef}
      className="trend-chart-svg"
      viewBox="0 0 1120 480"
      role="img"
      aria-labelledby="trend-chart-title trend-chart-description"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id="trend-chart-title">Analisi Trend {props.analysis.year}</title>
      <desc id="trend-chart-description">Volumi mensili per le categorie Social Subreason selezionate.</desc>
      <rect width="1120" height="480" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="66" y="21" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="15" fontWeight="700">
        Andamento mensile · {props.analysis.year}
      </text>

      {ticks.map((tick) => {
        const y = chart.y + chart.height - (tick / axis.maximum) * chart.height;
        return (
          <g key={tick}>
            <line x1={chart.x} x2={chart.x + chart.width} y1={y} y2={y} stroke="#dfe6ec" strokeWidth="1" />
            <text x={chart.x - 13} y={y + 4} textAnchor="end" fill="#70808d" fontFamily="Inter, Arial, sans-serif" fontSize="11">
              {formatNumber(tick)}
            </text>
          </g>
        );
      })}

      {props.analysis.months.map((month, monthIndex) => {
        const centerX = chart.x + monthWidth * monthIndex + monthWidth / 2;
        return (
          <g key={month.month}>
            {selected.map((category, seriesIndex) => {
              const value = month.values[category.index] ?? 0;
              const height = (value / axis.maximum) * chart.height;
              const x = centerX - groupWidth / 2 + seriesIndex * barWidth;
              const y = chart.y + chart.height - height;
              return (
                <rect
                  className="trend-chart-bar"
                  key={category.key}
                  x={x + 1}
                  y={y}
                  width={Math.max(2, barWidth - 2)}
                  height={height}
                  rx="2"
                  fill={colors[seriesIndex]}
                >
                  <title>{month.label}: {category.label}, {formatNumber(value)}</title>
                </rect>
              );
            })}
            <text x={centerX} y={chart.y + chart.height + 24} textAnchor="middle" fill="#566675" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="600">
              {SHORT_MONTHS[monthIndex]}
            </text>
          </g>
        );
      })}
      <line x1={chart.x} x2={chart.x + chart.width} y1={chart.y + chart.height} y2={chart.y + chart.height} stroke="#7d8b95" strokeWidth="1.2" />

      {selected.map((category, index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);
        const x = chart.x + column * 340;
        const y = 415 + row * 29;
        return (
          <g key={category.key}>
            <rect x={x} y={y - 11} width="13" height="13" rx="3" fill={colors[index]} />
            <text x={x + 21} y={y} fill="#405463" fontFamily="Inter, Arial, sans-serif" fontSize="11.5" fontWeight="600">
              {category.label.length > 39 ? `${category.label.slice(0, 37)}…` : category.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
});

export function OutputPage() {
  const { user } = useAuth();
  const [activeOutput, setActiveOutput] = useState<'trend' | 'case-inflow' | 'pec-social-subreason' | 'social-reason-entry' | 'territorial-analysis'>('trend');
  const svgRef = useRef<SVGSVGElement>(null);
  const seriesDragRef = useRef(false);
  const [availability, setAvailability] = useState<ProcessingAvailability | null>(null);
  const [year, setYear] = useState<number | ''>('');
  const [analysis, setAnalysis] = useState<TrendAnalysis | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [draggedSeriesKey, setDraggedSeriesKey] = useState<string | null>(null);
  const [seriesDropTarget, setSeriesDropTarget] = useState<{ key: string; position: 'before' | 'after' } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const loadAvailability = async () => {
      try {
        const data = user.demo
          ? demoAvailability
          : (await httpsCallable<undefined, ProcessingAvailability>(
              requireFirebaseFunctions(),
              'getProcessingAvailability',
            )()).data;
        if (!active) return;
        setAvailability(data);
        setYear((current) => current || outputReportYears(data, 'trend')[0] || '');
      } catch (cause) {
        if (!active) return;
        setError(errorMessage(cause));
        setStatus('error');
      }
    };
    void loadAvailability();
    return () => { active = false; };
  }, [user]);

  const loadAnalysis = useCallback(async (regenerate = false) => {
    if (!year) return;
    setStatus('loading');
    setError(null);
    setExportError(null);
    try {
      const result = user?.demo
        ? {
            ...demoTrendAnalysis(year),
            generation: {
              generatedAt: new Date().toISOString(),
              source: regenerate ? 'manual' as const : 'nightly' as const,
              cached: !regenerate,
              version: 1,
            },
          }
        : (await httpsCallable<{ year: number; regenerate: boolean }, TrendAnalysis>(
            requireFirebaseFunctions(),
            'getOutputTrendReport',
            OUTPUT_REPORT_CALLABLE_OPTIONS,
          )({ year, regenerate })).data;
      setAnalysis(result);
      setSelectedKeys(defaultSeriesKeys(result.categories));
      setStatus('ready');
    } catch (cause) {
      setError(errorMessage(cause));
      setStatus('error');
    }
  }, [user, year]);

  useEffect(() => {
    if (activeOutput !== 'trend' || !year || status !== 'idle') return;
    const timeout = window.setTimeout(() => void loadAnalysis(false), 0);
    return () => window.clearTimeout(timeout);
  }, [activeOutput, loadAnalysis, status, year]);

  const toggleCategory = (key: string) => {
    setSelectedKeys((current) => {
      if (current.includes(key)) {
        return current.length === 1 ? current : current.filter((item) => item !== key);
      }
      return current.length >= MAX_CHART_SERIES ? current : [...current, key];
    });
  };

  const exportSvg = () => {
    if (!svgRef.current || !analysis) return;
    setExportError(null);
    const serialized = svgElementMarkupForExport(svgRef.current);
    downloadBlob(new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], { type: 'image/svg+xml;charset=utf-8' }), `analisi-trend-${analysis.year}.svg`);
  };

  const exportPng = async () => {
    if (!svgRef.current || !analysis) return;
    setExportError(null);
    try {
      const serialized = svgElementMarkupForExport(svgRef.current);
      const image = new Image();
      image.decoding = 'async';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Immagine non generata'));
        image.src = svgMarkupDataUrl(serialized);
      });
      const canvas = document.createElement('canvas');
      canvas.width = 2240;
      canvas.height = 960;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas non disponibile');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 1));
      if (!png) throw new Error('PNG non generato');
      downloadBlob(png, `analisi-trend-${analysis.year}.png`);
    } catch {
      setExportError('Non è stato possibile esportare il grafico in PNG. Puoi usare il formato SVG.');
    }
  };

  const exportExcel = () => {
    if (!analysis) return;
    setExportError(null);
    try {
      const workbook = createTrendWorkbook(analysis, selectedKeys);
      const workbookBuffer = Uint8Array.from(workbook).buffer;
      downloadBlob(new Blob([workbookBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), trendWorkbookFileName(analysis.year));
    } catch {
      setExportError('Non è stato possibile creare il file Excel.');
    }
  };

  const visibleSeries = analysis
    ? selectedKeys.flatMap((key) => {
        const index = analysis.categories.findIndex((category) => category.key === key);
        return index >= 0 ? [{ category: analysis.categories[index], index }] : [];
      })
    : [];
  const selectedColors = seriesColors(selectedKeys);
  const orderedCategories = analysis
    ? [
        ...visibleSeries.map(({ category }) => category),
        ...analysis.categories.filter(({ key }) => !selectedKeys.includes(key)),
      ]
    : [];
  const trendYears = outputReportYears(availability, 'trend');

  return (
    <main className="dashboard-page output-page">
      <header className="page-heading output-heading">
        <div>
          <p className="app-eyebrow">Report generati</p>
          <h1>Report</h1>
          <p>Analizza i dati del Master, confronta i periodi ed esporta i risultati.</p>
        </div>
      </header>

      <nav className="output-tabs" aria-label="Tipi di report">
        <button type="button" className={activeOutput === 'trend' ? 'active' : ''} aria-current={activeOutput === 'trend' ? 'page' : undefined} onClick={() => setActiveOutput('trend')}>
          <BarChart3 size={19} />
          <span><strong>Analisi Trend</strong><small>Social Subreason per mese</small></span>
        </button>
        <button type="button" className={activeOutput === 'case-inflow' ? 'active' : ''} aria-current={activeOutput === 'case-inflow' ? 'page' : undefined} onClick={() => setActiveOutput('case-inflow')}>
          <Inbox size={19} />
          <span><strong>Case in entrata</strong><small>Entrata e chiusure mensili</small></span>
        </button>
        <button type="button" className={activeOutput === 'pec-social-subreason' ? 'active' : ''} aria-current={activeOutput === 'pec-social-subreason' ? 'page' : undefined} onClick={() => setActiveOutput('pec-social-subreason')}>
          <Inbox size={19} />
          <span><strong>Social Subreason PEC</strong><small>Stato operativo mensile</small></span>
        </button>
        <button type="button" className={activeOutput === 'social-reason-entry' ? 'active' : ''} aria-current={activeOutput === 'social-reason-entry' ? 'page' : undefined} onClick={() => setActiveOutput('social-reason-entry')}>
          <MessageSquareText size={19} />
          <span><strong>Ingresso Social Reason</strong><small>Stato e andamento giornaliero</small></span>
        </button>
        <button type="button" className={activeOutput === 'territorial-analysis' ? 'active' : ''} aria-current={activeOutput === 'territorial-analysis' ? 'page' : undefined} onClick={() => setActiveOutput('territorial-analysis')}>
          <MapPinned size={19} />
          <span><strong>Analisi territoriale</strong><small>Residenziale, Business e PA</small></span>
        </button>
      </nav>

      {activeOutput === 'case-inflow' ? (
        <CaseInflowPanel availability={availability} demo={user?.demo === true} />
      ) : activeOutput === 'pec-social-subreason' ? (
        <PecSocialSubreasonPanel availability={availability} demo={user?.demo === true} />
      ) : activeOutput === 'social-reason-entry' ? (
        <SocialReasonEntryPanel availability={availability} demo={user?.demo === true} />
      ) : activeOutput === 'territorial-analysis' ? (
        <TerritorialAnalysisPanel availability={availability} demo={user?.demo === true} />
      ) : (
        <>
      <section className="trend-period-card">
        <div className="trend-period-copy">
          <span><CalendarRange size={22} /></span>
          <div>
            <p>Periodo di analisi</p>
            <h2>Seleziona l’annualità</h2>
            <small>
              {analysis?.generation
                ? `${formatReportGeneration(analysis.generation)}. Il pulsante “Rigenera analisi” sovrascrive questa versione.`
                : 'I dati vengono aggregati dal file Master annuale presente su Drive.'}
            </small>
          </div>
        </div>
        <div className="trend-period-actions">
          <label>
            <span>Anno</span>
            <select value={year} disabled={!availability || status === 'loading'} onChange={(event) => {
              setYear(Number(event.target.value));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!trendYears.length && <option value="">Caricamento…</option>}
              {trendYears.map((availableYear) => <option value={availableYear} key={availableYear}>{availableYear}</option>)}
            </select>
          </label>
          <button className="button button-primary" type="button" onClick={() => void loadAnalysis(analysis !== null)} disabled={!year || status === 'loading'}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <BarChart3 size={17} />}
            {status === 'loading' ? 'Elaborazione…' : analysis ? 'Rigenera analisi' : 'Genera analisi'}
          </button>
          {analysis?.file.webViewLink && (
            <a className="button button-secondary" href={analysis.file.webViewLink} target="_blank" rel="noreferrer">
              <ExternalLink size={15} /> Apri su Drive
            </a>
          )}
        </div>
      </section>

      {status === 'idle' && (
        <section className="trend-empty-state">
          <span><BarChart3 size={24} /></span>
          <div><strong>L’analisi è pronta per essere generata</strong><p>Scegli l’anno e carica i dati mensili del Master.</p></div>
        </section>
      )}

      {status === 'loading' && (
        <section className="trend-empty-state loading">
          <span><LoaderCircle className="spin" size={24} /></span>
          <div><strong>Sto leggendo il Master {year}.xlsx</strong><p>L’aggregazione avviene sulla colonna Social Subreason.</p></div>
        </section>
      )}

      {status === 'error' && (
        <section className="trend-empty-state error">
          <span><AlertTriangle size={24} /></span>
          <div><strong>Analisi non disponibile</strong><p>{error}</p></div>
          <button type="button" className="button button-secondary" onClick={() => void loadAnalysis(false)} disabled={!year}><RefreshCw size={16} /> Riprova</button>
        </section>
      )}

      {status === 'ready' && analysis && (
        <>
          <section className="trend-summary-grid" aria-label="Sintesi annuale">
            <article><span>Segnalazioni classificate</span><strong>{formatNumber(analysis.classifiedRows)}</strong><small>con Social Subreason valorizzata</small></article>
            <article><span>Categorie</span><strong>{formatNumber(analysis.categories.length)}</strong><small>valori distinti nel Master</small></article>
            <article><span>Righe annuali</span><strong>{formatNumber(analysis.sourceRows)}</strong><small>{formatNumber(analysis.unclassifiedRows)} senza classificazione</small></article>
            <article><span>Categoria principale</span><strong>{analysis.categories[0]?.label ?? '—'}</strong><small>{formatNumber(analysis.categories[0]?.total ?? 0)} segnalazioni</small></article>
          </section>

          <ReportDataWarnings warnings={analysis.dataWarnings} />

          <section className="trend-chart-card">
            <header>
              <div>
                <p>Grafico</p>
                <h2>Andamento mensile</h2>
                <span>Seleziona da una a cinque categorie da confrontare.</span>
              </div>
              <div className="trend-export-actions">
                <button type="button" className="button button-secondary" onClick={() => void exportPng()}><ImageDown size={15} /> PNG</button>
                <button type="button" className="button button-secondary" onClick={exportSvg}><FileDown size={15} /> SVG</button>
              </div>
            </header>
            <div className="trend-chart-layout">
              <div className="trend-chart-scroll">
                <TrendChart analysis={analysis} selectedKeys={selectedKeys} ref={svgRef} />
              </div>
              <aside className="trend-series-panel">
                <div><strong>Serie del grafico</strong><span>{selectedKeys.length}/{MAX_CHART_SERIES} selezionate</span></div>
                <div className="trend-series-list">
                  {orderedCategories.map((category) => {
                    const selectedIndex = selectedKeys.indexOf(category.key);
                    const selected = selectedIndex >= 0;
                    const disabled = !selected && selectedKeys.length >= MAX_CHART_SERIES;
                    const dropPosition = seriesDropTarget?.key === category.key ? seriesDropTarget.position : null;
                    return (
                      <button
                        type="button"
                        key={category.key}
                        className={[
                          selected ? 'selected' : '',
                          draggedSeriesKey === category.key ? 'dragging' : '',
                          dropPosition ? `drop-${dropPosition}` : '',
                        ].filter(Boolean).join(' ')}
                        disabled={disabled}
                        draggable={selected}
                        onClick={() => {
                          if (seriesDragRef.current) return;
                          toggleCategory(category.key);
                        }}
                        onDragStart={(event) => {
                          if (!selected) {
                            event.preventDefault();
                            return;
                          }
                          seriesDragRef.current = true;
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', category.key);
                          setDraggedSeriesKey(category.key);
                        }}
                        onDragOver={(event) => {
                          if (!selected || !draggedSeriesKey || draggedSeriesKey === category.key) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = 'move';
                          const bounds = event.currentTarget.getBoundingClientRect();
                          const position = event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after';
                          setSeriesDropTarget({ key: category.key, position });
                        }}
                        onDrop={(event) => {
                          if (!selected || !draggedSeriesKey || draggedSeriesKey === category.key) return;
                          event.preventDefault();
                          const bounds = event.currentTarget.getBoundingClientRect();
                          const placeAfter = event.clientY >= bounds.top + bounds.height / 2;
                          setSelectedKeys((current) => moveSeries(current, draggedSeriesKey, category.key, placeAfter));
                          setSeriesDropTarget(null);
                        }}
                        onDragEnd={() => {
                          setDraggedSeriesKey(null);
                          setSeriesDropTarget(null);
                          window.setTimeout(() => { seriesDragRef.current = false; }, 0);
                        }}
                      >
                        <i style={{ background: selected ? selectedColors[selectedIndex] : undefined }}>{selected && <Check size={11} />}</i>
                        <span><strong>{category.label}</strong><small>{formatNumber(category.total)} segnalazioni</small></span>
                        {selected && (
                          <span className="trend-series-drag-handle" title="Trascina per riordinare" onClick={(event) => event.stopPropagation()}>
                            <GripVertical size={15} aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </aside>
            </div>
          </section>

          <section className="trend-table-card">
            <header>
              <div>
                <p>Tabella</p>
                <h2>Volumi per mese e Social Subreason</h2>
                <span>{visibleSeries.length} categorie selezionate, senza paginazione.</span>
              </div>
              <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
            </header>
            <div className="trend-table-scroll">
              <table className="trend-table">
                <thead>
                  <tr>
                    <th>Mesi</th>
                    {visibleSeries.map(({ category }) => <th key={category.key}><span>{category.label}</span><small>{formatNumber(category.total)} totali</small></th>)}
                  </tr>
                </thead>
                <tbody>
                  {analysis.months.map((month) => (
                    <tr key={month.month}>
                      <th scope="row">{month.label} {String(analysis.year).slice(-2)}</th>
                      {visibleSeries.map(({ category, index }) => {
                        const value = month.values[index] ?? 0;
                        return <td key={category.key} className={value === 0 ? 'zero' : ''}>{formatNumber(value)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Totale {analysis.year}</th>
                    {visibleSeries.map(({ category }) => <td key={category.key}>{formatNumber(category.total)}</td>)}
                  </tr>
                </tfoot>
              </table>
            </div>
            <footer><Table2 size={14} /> L’Excel rispetta le categorie selezionate e mantiene valori numerici, filtri e intestazione bloccata.</footer>
          </section>

          {exportError && <div className="trend-export-error" role="alert"><AlertTriangle size={15} /> {exportError}</div>}
        </>
      )}
        </>
      )}
    </main>
  );
}
